import React, { useState, useEffect } from 'react';
import { X, Loader2, Mail, MapPin, CheckCircle, ArrowUpRight } from 'lucide-react';
import UIPhoneInput from 'components/core/phone-input/phone-input';
import { Value } from 'react-phone-number-input';
import { validatePhoneNumber } from 'utils/validation/phone-validation';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { useTheme } from 'components/core/theme-provider';
import { primaryButtonClass } from '../constants/button-styles';

interface ContactFormData {
  name: string;
  company: string;
  email: string;
  phone?: string;
  message: string;
}

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ContactFormData) => Promise<any>;
  title?: string;
  initialData?: Partial<ContactFormData>;
  companyRequired?: boolean;
}

const fieldClass =
  'w-full rounded-2xl border border-[#e8e4d8] bg-[#faf9f5] px-4 text-sm text-[#171b23] placeholder:text-[#8b8a84] shadow-none outline-none transition-colors focus:border-[#171b23] focus:bg-white dark:border-border dark:bg-muted/40 dark:text-foreground dark:placeholder:text-muted-foreground dark:focus:border-foreground dark:focus:bg-background';

const inputClass = cn(fieldClass, 'h-14');

const phoneWrapClass = cn(
  inputClass,
  'flex items-center overflow-hidden',
  '[&_.PhoneInput]:flex [&_.PhoneInput]:h-full [&_.PhoneInput]:w-full [&_.PhoneInput]:items-center [&_.PhoneInput]:gap-1.5',
  '[&_.PhoneInputInput]:h-full [&_.PhoneInputInput]:min-w-0 [&_.PhoneInputInput]:flex-1 [&_.PhoneInputInput]:border-0 [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:p-0 [&_.PhoneInputInput]:outline-none',
  '[&_.PhoneInputCountry]:shrink-0',
  '[&_.PhoneInputCountrySelectButton]:!h-full [&_.PhoneInputCountrySelectButton]:!rounded-none [&_.PhoneInputCountrySelectButton]:!px-0 [&_.PhoneInputCountrySelectButton]:!shadow-none'
);

const ContactModal: React.FC<ContactModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  initialData,
  companyRequired = false,
}) => {
  const { t } = useTranslation();
  const isDark = useTheme().resolvedTheme === 'dark';

  const createContactSchema = () =>
    z.object({
      name: z.string().min(1, t('NAME_IS_REQUIRED')).max(50, t('NAME_IS_TOO_LONG')),
      company: companyRequired
        ? z.string().min(1, t('COMPANY_IS_REQUIRED')).max(100, t('COMPANY_IS_TOO_LONG'))
        : z.string().max(100, t('COMPANY_IS_TOO_LONG')).optional().or(z.literal('')),
      email: z
        .string()
        .min(1, t('EMAIL_IS_REQUIRED'))
        .email(t('INVALID_EMAIL_ADDRESS'))
        .refine((val) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val), {
          message: t('INVALID_EMAIL_ADDRESS'),
        }),
      phone: z
        .string()
        .refine((val) => !val || /^\+[1-9]\d{1,14}$/.test(val), {
          message: t('INVALID_PHONE_NUMBER'),
        })
        .refine(validatePhoneNumber, { message: t('PHONE_NUMBER_TOO_SHORT') })
        .optional()
        .or(z.literal('')),
      message: z.string().min(1, t('MESSAGE_IS_REQUIRED')),
    });

  const emptyForm: ContactFormData = {
    name: initialData?.name || '',
    company: initialData?.company || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    message: initialData?.message || '',
  };

  const [formData, setFormData] = useState<ContactFormData>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (isOpen && !isSuccess) {
      setFormData({
        name: initialData?.name || '',
        company: initialData?.company || '',
        email: initialData?.email || '',
        phone: initialData?.phone || '',
        message: initialData?.message || '',
      });
      setErrors({});
      setLoading(false);
    }
    if (!isOpen) setIsSuccess(false);
  }, [isOpen, initialData, isSuccess]);

  const validate = () => {
    try {
      createContactSchema().parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) newErrors[err.path[0].toString()] = err.message;
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      setLoading(true);
      const isOnlyCode = formData.phone?.match(/^\+\d{1,4}$/) && formData.phone.length <= 4;
      await onSubmit({
        ...formData,
        phone: isOnlyCode ? '' : formData.phone,
      });
      setIsSuccess(true);
    } catch (error) {
      console.error('Submission failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const setField = (key: keyof ContactFormData, value: string) =>
    setFormData((prev) => ({ ...prev, [key]: value }));

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        hideClose
        className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-[640px] overflow-hidden rounded-3xl border-[#e8e4d8] p-0 shadow-2xl dark:border-border"
      >
        <DialogTitle className="sr-only">{title || t('CONTACT_SALES', 'Contact Sales')}</DialogTitle>

        <div className="relative bg-gradient-to-b from-[#fff7b8] to-white px-6 pb-5 pt-6 dark:from-amber-900/35 dark:to-card">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 rounded-full p-1.5 text-[#565f6b] transition-colors hover:bg-black/5 hover:text-[#171b23] dark:text-muted-foreground dark:hover:bg-white/10 dark:hover:text-foreground"
            aria-label={t('CLOSE', 'Close')}
          >
            <X className="h-4 w-4" />
          </button>
          <p className="pr-8 font-sans text-[1.65rem] font-bold leading-tight text-[#171b23] dark:text-foreground">
            {title || t('CONTACT_SALES', 'Contact Sales')}
          </p>
          <p className="mt-2 pr-8 font-sans text-sm leading-5 text-[#565f6b] dark:text-muted-foreground">
            {t('FILL_OUT_FORM')}
          </p>
        </div>

        <div className="max-h-[min(70vh,560px)] overflow-y-auto px-6 pb-6">
          {isSuccess ? (
            <div className="flex flex-col items-center py-8 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10">
                <CheckCircle className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h3 className="mb-2 font-sans text-xl font-bold text-[#171b23] dark:text-foreground">
                {t('MESSAGE_SENT_SUCCESSFULLY') || 'Message sent'}
              </h3>
              <p className="mb-6 max-w-xs text-sm leading-5 text-[#565f6b] dark:text-muted-foreground">
                {t('CONTACT_SUCCESS_MESSAGE') ||
                  "Thanks — we'll get back to you within 24 hours."}
              </p>
              <Button
                onClick={onClose}
                className={cn('w-full rounded-full py-6 text-base font-semibold', primaryButtonClass(isDark))}
              >
                {t('CLOSE', 'Close')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3.5">
              <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium text-[#565f6b] dark:text-muted-foreground">
                    {t('NAME')} *
                  </span>
                  <input
                    required
                    type="text"
                    placeholder={t('ENTER_YOUR_NAME')}
                    value={formData.name}
                    onChange={(e) => setField('name', e.target.value)}
                    className={inputClass}
                  />
                  {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                </label>
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium text-[#565f6b] dark:text-muted-foreground">
                    {t('COMPANY')}
                    {companyRequired && ' *'}
                  </span>
                  <input
                    type="text"
                    placeholder={t('ORGANIZATION_NAME_PLACEHOLDER')}
                    value={formData.company}
                    onChange={(e) => setField('company', e.target.value)}
                    className={inputClass}
                  />
                  {errors.company && <p className="text-xs text-destructive">{errors.company}</p>}
                </label>
              </div>

              <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium text-[#565f6b] dark:text-muted-foreground">
                    {t('EMAIL')} *
                  </span>
                  <input
                    type="email"
                    placeholder={t('ENTER_YOUR_EMAIL')}
                    value={formData.email}
                    onChange={(e) => setField('email', e.target.value)}
                    className={inputClass}
                  />
                  {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                </label>
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium text-[#565f6b] dark:text-muted-foreground">
                    {t('PHONE')}
                  </span>
                  <div className={phoneWrapClass}>
                    <UIPhoneInput
                      value={formData.phone as Value}
                      onChange={(value: Value) => setField('phone', (value as string) || '')}
                      placeholder={t('PHONE_NUMBER_PLACEHOLDER')}
                      defaultCountry="CH"
                      countryCallingCodeEditable={false}
                      international
                      className="!h-full min-w-0 flex-1 border-0 bg-transparent p-0 shadow-none"
                    />
                  </div>
                  {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
                </label>
              </div>

              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-[#565f6b] dark:text-muted-foreground">
                  {t('MESSAGE')} *
                </span>
                <textarea
                  placeholder={t('MESSAGE_PLACEHOLDER')}
                  value={formData.message}
                  onChange={(e) => setField('message', e.target.value)}
                  rows={4}
                  className={cn(fieldClass, 'py-3 resize-none')}
                />
                {errors.message && <p className="text-xs text-destructive">{errors.message}</p>}
              </label>

              <Button
                variant="default"
                onClick={handleSubmit}
                disabled={loading}
                className={cn(
                  'mt-2 flex w-full items-center justify-center gap-2 rounded-full py-6 text-base font-semibold disabled:opacity-50',
                  primaryButtonClass(isDark)
                )}
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
                {loading ? t('SENDING') : t('SEND')}
              </Button>

              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 pt-1 text-xs text-[#565f6b] dark:text-muted-foreground">
                <a
                  href="mailto:info@selisegroup.com"
                  className="inline-flex items-center gap-1.5 hover:text-[#171b23] dark:hover:text-foreground"
                >
                  <Mail className="h-3.5 w-3.5" />
                  info@selisegroup.com
                </a>
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" />
                  Rotkreuz, {t('SWITZERLAND')}
                </span>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
